/**
 * Main App Component
 * Handles routing and global state
 */

import React from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ToastProvider } from "./context/ToastContext";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ConfigProvider } from "./context/ConfigContext";

// Layout Components
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import ErrorBoundary from "./components/ErrorBoundary";

// Pages
import Home from "./pages/Home";
import About from "./pages/About";
import Experience from "./pages/Experience";
import JobsDetail from "./pages/JobsDetail";
import TeachingBlue from "./pages/TeachingBlue";
import CourseViewer from "./pages/CourseViewer";
import LessonViewer from "./pages/LessonViewer";
import Projects from "./pages/Projects";
import PortfolioDetail from "./pages/PortfolioDetail";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import AdminDashboard from "./pages/AdminDashboard";
import SuperAdmin from "./pages/SuperAdmin";
import UserSettings from "./pages/UserSettings";
import NotFound from "./pages/NotFound";
import VerifyDiscord from "./pages/VerifyDiscord";
import LinkTree from "./pages/LinkTree3D";
// Consulting page removed — redirects to tsi400.com

// Protected Route Component
import ProtectedRoute from "./components/ProtectedRoute";

// Global Styles
import GlobalStyles from "./styles/GlobalStyles";

// Analytics
import useAnalytics from "./hooks/useAnalytics";

// Redirect to external URL (for old /consulting bookmarks)
function ExternalRedirect({ to }) {
  React.useEffect(() => { window.location.replace(to); }, [to]);
  return null;
}

// Inner component that renders the app content
function AppContent() {
  const location = useLocation();
  const isLinkTreePage = location.pathname === '/r/links' || location.pathname.startsWith('/l');
  
  return (
    <>
      <GlobalStyles />
      {!isLinkTreePage && <Navbar />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/jobs" element={<Experience />} />
        <Route path="/jobs/:id" element={<JobsDetail />} />
        <Route path="/teaching" element={<TeachingBlue />} />
        <Route path="/course/:courseId" element={<CourseViewer />} />
        <Route
          path="/lessons"
          element={
            <ProtectedRoute requiredRole="student">
              <LessonViewer />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lessons/:lessonId"
          element={
            <ProtectedRoute requiredRole="student">
              <LessonViewer />
            </ProtectedRoute>
          }
        />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:id" element={<PortfolioDetail />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:id" element={<BlogDetail />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/consulting" element={<ExternalRedirect to="https://tsi400.com" />} />
        <Route path="/r/links" element={<LinkTree />} />
        <Route path="/l" element={<LinkTree />} />
        <Route path="/verify/discord" element={<VerifyDiscord />} />
        <Route path="/login" element={<Login />} />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <UserSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin"
          element={
            <ProtectedRoute>
              <SuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!isLinkTreePage && <Footer />}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider>
          <ToastProvider>
            <AuthProvider>
              <ConfigProvider>
                <Router>
                  <AppContentWithAnalytics />
                </Router>
              </ConfigProvider>
            </AuthProvider>
          </ToastProvider>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

// Separate component that uses analytics - ensures Router is fully initialized
function AppContentWithAnalytics() {
  useAnalytics();
  return <AppContent />;
}

export default App;
